import { isEmpty, get } from 'lodash';
import {
  DOCUMENT,
  VEHICULE_EXPERTISE_FIELDS,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import validate from 'containers/services/validateForm';
import {
  EXPERTISE_ACCOUNTING_ID,
  EXPERTISE_MECHANIC_ID,
  EXPERTISE_MEDICAL_ID,
} from 'containers/ExpertExpertise/ConcernedExpertise/constants';
import { FeeNoteType, VictimType } from 'typings/globalTypes';
import { Listings } from 'containers/AppContainer/typings';
import { GLOBAL_STATUS } from 'services/constants';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';

export const medicalExpertiseConstraint = (victimExpertise: any) => ({
  [VICTIM_EXPERTISE_FIELDS.ITT]: { presence: true },
  [VICTIM_EXPERTISE_FIELDS.IPP]: { presence: true },
  [VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES]: { presence: false },
  [VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL]: { presence: true },
  [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]: { presence: true },
  [VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE]: {
    presence: !victimExpertise[
      VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE
    ],
  },
  [VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE]: {
    presence: !victimExpertise[
      VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
    ],
  },
  [VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE]: {
    presence: !victimExpertise[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
  },
  [VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE]: {
    presence: !victimExpertise[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
  },
});
export const accountingExpertiseConstraint = (victimExpertise: any) => ({
  [VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]: { presence: true },
});
export const medicalExpertisesValidator = (expertise: any) => {
  return validate(expertise, medicalExpertiseConstraint(expertise));
};
export const accountingExpertisesValidator = (expertise: any) => {
  return validate(expertise, accountingExpertiseConstraint(expertise));
};
export const expertiseDocumentConstraint = {
  [DOCUMENT.NAME]: { presence: true },
  [DOCUMENT.TYPE]: { presence: true },
  [DOCUMENT.URL]: { presence: true },
};
export const expertiseDocumentValidator = (expertiseDocument: any) => {
  return validate(expertiseDocument, expertiseDocumentConstraint);
};
export const shouldDisplayActionButton = (expertises: any, sagis_code: any) => {
  return isEmpty(
    expertises.filter((element: any) => element.createdBy === sagis_code),
  );
};
export const renderFilterExpertiseTypeByMissionExpertise = (
  missionExpertise: number,
) => {
  if (Number(missionExpertise) === EXPERTISE_ACCOUNTING_ID) {
    return 1;
  } else {
    return EXPERTISE_ACCOUNTING_ID;
  }
};
export const renderFilterConcernedPartyTypeByMissionExpertise = (
  missionExpertise: number,
  vehicules: any,
  vehicle: any,
  victims: any,
  victim: VictimType,
) => {
  if (
    (Number(missionExpertise) === EXPERTISE_ACCOUNTING_ID &&
      !isEmpty(victim)) ||
    Number(missionExpertise) === EXPERTISE_MEDICAL_ID
  ) {
    return victims.filter((item: any) => item.id === get(victim, 'id'));
  } else if (
    Number(missionExpertise) === EXPERTISE_MECHANIC_ID ||
    (Number(missionExpertise) === EXPERTISE_ACCOUNTING_ID && !isEmpty(vehicle))
  ) {
    return vehicules.filter((item: any) => item.id === get(vehicle, 'id'));
  }
};
export const mechanicalExpertiseConstraint = (vehicleExpertise: any) => ({
  [VEHICULE_EXPERTISE_FIELDS.DAMAGE_AMOUNT]: { presence: true },
});
export const mechanicalExpertisesValidator = (expertise: any) => {
  return validate(expertise, mechanicalExpertiseConstraint(expertise));
};

export const getValueFromListings = (
  listings: Listings,
  nameOfList: string,
  attribute: string,
  value: string,
  returnedAttribute: string,
) => {
  const filtredList = get(listings, `${nameOfList}`, []);
  return get(
    filtredList.find(
      (el: any) => String(get(el, `${attribute}`)) === String(value),
    ),
    `${returnedAttribute}`,
  );
};

export const getExpertInfo = (listings: Listings, expertCode: string) => {
  const expertInfo = get(listings, 'intervenants')?.find(
    (item: any) => Number(item.ItemKey) === Number(expertCode),
  );

  return expertInfo;
};

const getPrejudiceValue = (
  listings: Listings,
  expertise: any,
  key: string,
): string => {
  const value = getValueFromListings(
    listings,
    key,
    'percentage',
    (get(expertise, key) as unknown) as string,
    'title',
  );
  return value !== 'Inexistant' ? value : 'Inexistant';
};

export const getValueOfOtherPrejudice = (
  listings: Listings,
  expertise: any,
) => {
  for (const key of ['careerPrejudice', 'schoolingPrejudice']) {
    const value = getPrejudiceValue(listings, expertise, key);
    const keyTitle =
      key === 'careerPrejudice' ? 'Sur la carrière' : 'Sur la scolarité';
    if (value !== 'Inexistant') return keyTitle + ', ' + value;
  }
  return 'Inexistant';
};

export const getValueOfPrejudice = (listings: Listings, expertise: any) => {
  const withConsequence = getPrejudiceValue(
    listings,
    expertise,
    'aestheticDamageWithConsequence',
  );

  const withoutConsequence = getPrejudiceValue(
    listings,
    expertise,
    'aestheticDamageWithoutConsequence',
  );
  if (withConsequence !== undefined && withConsequence !== 'Inexistant') {
    return 'Avec conséquence sur la carrière' + ', ' + withConsequence;
  }

  if (withoutConsequence !== undefined && withoutConsequence !== 'Inexistant') {
    return 'Sans conséquence sur la carrière' + ', ' + withoutConsequence;
  }
  return 'Inexistant';
};

export const getPendingFeeNotesByExpertise = (
  feeNotes: FeeNoteType[],
  missionExpertiseVictimId: number,
) => {
  return feeNotes.filter(
    (el: FeeNoteType) =>
      el.feeNoteMission.missionExpertiseVictimId === missionExpertiseVictimId &&
      (el.status === GLOBAL_STATUS.PENDING ||
        el.status === GLOBAL_STATUS.REJECTED) &&
      el.codeIntervenant === currentIntervenantStorage?.get()?.sagis_code,
  );
};
